/* =============================================================================
   VIEWS / ADMIN / MENU-CONFIGURATOR — Gericht-Konfigurator (S155 / Phase D1)
   =============================================================================
   EIN integrierter Erstell-/Bearbeitungspfad: „+ Gericht" und „Details/bearbeiten"
   aus der Speisekarte öffnen diesen Konfigurator (löst das alte dünne Formular +
   den separaten Rezept-Editor ab und vereint sie). Eine Datenquelle (menu_items +
   recipe_items). Assistiert (D1): klick-für-klick bauen + Live-Werte + Speichern.

   Abschnitte: Identität · Options-Gruppen (klickbar → meta) · Basis-Rezept
   (eingebetteter Rezept-Editor S151/S154) · Live-Panel (Nährwerte/Allergene/Tags)
   · Constraint-Check (assistiv) · Speichern (Upsert menu_item + Rezept).

   Hinter Flag `ingredientsDb`. Non-destruktive Teil-Renders; Inline-Handler ohne
   window.func().
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { DIETARY_TAGS, ALLERGENS_EU14 } from '../../core/nutrition.js';
import { buildCategoryTree } from '../../core/menu-categories.js';
import {
  optionGroupsFromMenu, metaFromGroupSelection, groupSelectionFromMeta,
  evalConstraints, buildMenuItemPayload,
  constraintFilterActive, ingredientMeetsTargets, filterOptionsByTargets,
  defaultRulesForRole, optionSetsFromMeta
} from '../../core/menu-configurator.js';
import {
  loadRecipeData, renderRecipeInnerHtml, getRecipeCleanRows,
  setRecipeChangeHook, renderNutritionBoxHtml, setRecipeRows, setIngredientFilter
} from './menu-recipe-editor.js';
import { generateDish } from '../../core/dish-generator.js';
import { costEvaluation, ampelFor, targetPctFor, formatEuro, formatPct, AMPEL_COLORS } from '../../core/food-cost.js';

// S158/S166 — Ampel-Farben (Food-Cost-Band), zentral aus core/food-cost.js.
const AMPEL_COLOR = AMPEL_COLORS;
const AMPEL_LABEL = { green: 'im Ziel', yellow: 'grenzwertig', red: 'über Ziel', unknown: 'Daten fehlen' };

function globalFoodCostTarget() {
  try {
    const st = window.App && window.App.storage && window.App.storage._getInternalState && window.App.storage._getInternalState();
    const v = st && st.settings && st.settings.foodCostTargetPct;
    if (v != null && isFinite(Number(v)) && Number(v) > 0) return Number(v);
  } catch (_) {}
  return 30;
}

const ITEM_TYPES = [
  { v: 'main', label: 'Hauptgericht' }, { v: 'side', label: 'Beilage' },
  { v: 'drink', label: 'Getränk' }, { v: 'extra', label: 'Extra (Topping)' },
  { v: 'sauce', label: 'Soße' }, { v: 'option', label: 'Option (Brötchen/Patty/Dressing)' }
];
// Karten-Diät-Tags (LMIV: nur manuell bestätigt) — gleiche Tokens wie der Menü-Editor.
const DISH_DIET_TAGS = ['vegan', 'vegetarisch', 'fisch', 'rindfleisch', 'hähnchen', 'scharf', 'halal', 'glutenfrei'];
const ALLERGEN_LABEL = Object.fromEntries(ALLERGENS_EU14.map(a => [a.key, a.label]));
const TAG_LABEL = Object.fromEntries(DIETARY_TAGS.map(t => [t.key, t.label]));

let _draft = null;
let _categories = [];
let _groups = [];
let _selection = [];
let _targets = { vegan: false, vegetarian: false, gluten_free: false, lactose_free: false, maxKcal: '' };
// S156b/B — selektives Re-Roll: was hat der User manuell gesetzt (bleibt bei „🎲 Zufällig
// konfigurieren" erhalten)? Generator-gefüllte Werte sind NICHT touched.
let _touched = { name: false, category: false, options: false, baseRows: false };

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }
function menuState() {
  const st = storage() && storage()._getInternalState && storage()._getInternalState();
  return (st && st.menu) || [];
}
function currentMeta() { return metaFromGroupSelection(_selection, _draft.meta || {}); }

// -----------------------------------------------------------------------------
// Präsentation: 'modal' (Menü-Editor, unverändert) | 'slideover' (Studio, S187 Cut B)
// EIN Code-Pfad (renderConfiguratorHtml + cfg*-Handler); nur Mount-Ziel unterscheidet.
// -----------------------------------------------------------------------------
let _present = 'modal';
let _dirty = false;            // ungespeicherte Änderungen (nur für Slide-over-Bestätigung)
let _slideoverKeyHandler = null;

function _cfgContentEl() {
  return _present === 'slideover'
    ? document.getElementById('cfg-slideover-content')
    : document.getElementById('modal-content');
}

function _ensureSlideover() {
  let ov = document.getElementById('cfg-slideover');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'cfg-slideover';
    ov.className = 'cfg-slideover-overlay';
    ov.innerHTML = `<div class="cfg-slideover-panel" role="dialog" aria-modal="true"><div id="cfg-slideover-content"></div></div>`;
    document.body.appendChild(ov);
    ov.addEventListener('click', (e) => { if (e.target === ov) _requestCloseSlideover(); }); // Außenklick
  }
  if (!_slideoverKeyHandler) {
    _slideoverKeyHandler = (e) => { if (e.key === 'Escape') _requestCloseSlideover(); };
    document.addEventListener('keydown', _slideoverKeyHandler, true);
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => ov.classList.add('open'));
  else ov.classList.add('open');
  return document.getElementById('cfg-slideover-content');
}

function _closeSlideover() {
  const ov = document.getElementById('cfg-slideover');
  if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
  if (_slideoverKeyHandler) { document.removeEventListener('keydown', _slideoverKeyHandler, true); _slideoverKeyHandler = null; }
  setRecipeChangeHook(null);
  setIngredientFilter(null);
  _present = 'modal';
  _dirty = false;
}

/** Schließen mit Draft-Schutz (×/Escape/Außenklick). */
async function _requestCloseSlideover() {
  if (_dirty && !(await window.styledConfirm({ title: 'Ungespeicherte Änderungen verwerfen?', danger: true, confirmLabel: 'Verwerfen' })).confirmed) return;
  _closeSlideover();
}

// -----------------------------------------------------------------------------
// Öffnen
// -----------------------------------------------------------------------------
export async function openConfigurator(menuItemId, opts) {
  if (!flagOn()) return;
  _present = (opts && opts.present === 'slideover') ? 'slideover' : 'modal';

  let content;
  if (_present === 'slideover') {
    content = _ensureSlideover();
    if (!content) _present = 'modal';   // Fallback auf Modal, falls Panel-Mount scheitert
  }
  if (_present === 'modal') {
    const overlay = document.getElementById('modal-overlay');
    content = document.getElementById('modal-content');
    if (!content || !overlay) return;
    overlay.classList.add('active');
  }
  content.innerHTML = `<div class="modal-header"><h2>Gericht-Konfigurator</h2><button class="modal-close" onclick="cfgCancel()">×</button></div><p class="hint">Lade…</p>`;

  try { _categories = await storage().menuCategories.list(); } catch (_) { _categories = []; }
  const existing = menuItemId ? menuState().find(m => m.id === menuItemId) : null;
  _draft = existing ? {
    id: existing.id, name: existing.name || '', categoryId: existing.categoryId || '', subcategoryId: existing.subcategoryId || '',
    category: existing.category || '', itemType: existing.itemType || 'main', price: existing.price ?? 0,
    unitLabel: existing.unitLabel || '', description: existing.description || '', available: existing.available !== false,
    meta: (existing.meta && typeof existing.meta === 'object') ? { ...existing.meta } : {},
    dietaryTags: Array.isArray(existing.dietaryTags) ? existing.dietaryTags.slice() : [],
    ingredientsText: Array.isArray(existing.ingredients) ? existing.ingredients.join(', ') : '',
    uberItemId: existing.uberItemId || ''
  } : {
    id: null, name: '', categoryId: '', subcategoryId: '', category: '', itemType: 'main', price: 10,
    unitLabel: '', description: '', available: true, meta: {}, dietaryTags: [], ingredientsText: '', uberItemId: ''
  };

  await loadRecipeData(menuItemId || null);
  if (window.App && window.App.nutritionCache) {
    try { await window.App.nutritionCache.ensureLoaded(); } catch (_) {}
  }
  _groups = optionGroupsFromMenu(menuState());
  _selection = groupSelectionFromMeta(_draft.meta, _groups);
  _targets = { vegan: false, vegetarian: false, gluten_free: false, lactose_free: false, maxKcal: '' };

  // Bestehende (geladene) Werte gelten als „gesetzt" → werden beim Lücken-Füllen nicht
  // überschrieben. Neues Gericht: alles offen.
  _touched = {
    name: !!_draft.name,
    category: !!(_draft.categoryId || _draft.category),
    options: !!(_draft.meta && Object.keys(_draft.meta).length),
    baseRows: getRecipeCleanRows().length > 0
  };

  setRecipeChangeHook(onRecipeUserChange);
  setIngredientFilter(null); // S156c — frische Maske: kein aktives Ziel → kein Filter
  content.innerHTML = renderConfiguratorHtml();
  refreshLive();
  _dirty = false;            // frisch geladen → sauber
}

// User-Änderung am eingebetteten Rezept → Basis gilt als „gesetzt" (S156b/B) + Live neu.
// (Generator setzt das Rezept via setRecipeRows, das den Hook NICHT feuert.)
function onRecipeUserChange() {
  _touched.baseRows = true;
  _dirty = true;
  refreshLive();
}

// -----------------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------------
function renderConfiguratorHtml() {
  return `
    <div class="modal-header">
      <h2>${_draft.id ? 'Gericht bearbeiten' : 'Neues Gericht'}</h2>
      <button class="modal-close" onclick="cfgCancel()">×</button>
    </div>
    <div style="display:grid; grid-template-columns:1fr; gap:16px;">
      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; background:var(--bg-soft,#f6f6f6); border-radius:8px; padding:8px 12px;">
        <button class="btn btn-secondary btn-sm" onclick="cfgGenerate()">🎲 Zufällig konfigurieren</button>
        <button class="btn btn-ghost btn-sm" onclick="cfgGenerateAll()">🎲 Alles neu</button>
        <span class="hint" style="font-size:.8rem;">„Zufällig konfigurieren" füllt nur die noch offenen Felder (deine Eingaben bleiben); „Alles neu" würfelt komplett. Beides respektiert die Ziel-Vorgaben unten — alles bleibt frei anpassbar.</span>
      </div>
      ${renderIdentityHtml()}
      ${renderGroupsHtml()}
      <div>
        <label class="med-label">Basis-Rezept (Zutaten + Bauanleitung)</label>
        <div id="cfg-recipe">${renderRecipeInnerHtml({ withBox: false })}</div>
      </div>
      <div id="cfg-live">${renderLiveHtml(null, null)}</div>
      ${renderAdvancedHtml()}
    </div>
    <div class="btn-row" style="margin-top:16px;">
      <button class="btn btn-primary" onclick="cfgSave()">Gericht speichern</button>
      <button class="btn btn-ghost" onclick="cfgCancel()">Abbrechen</button>
    </div>`;
}

function renderIdentityHtml() {
  const tree = buildCategoryTree(_categories);
  const KIND_LABEL = { menu: 'Menü', option: 'Optionen', extra: 'Extras' };
  const topOpts = ['menu', 'option', 'extra'].map(k => {
    const tops = tree.filter(t => t.kind === k);
    if (!tops.length) return '';
    return `<optgroup label="${KIND_LABEL[k]}">${tops.map(t =>
      `<option value="${htmlEscape(t.id)}" ${t.id === _draft.categoryId ? 'selected' : ''}>${t.icon ? htmlEscape(t.icon) + ' ' : ''}${htmlEscape(t.name)}</option>`).join('')}</optgroup>`;
  }).join('');
  const selTop = tree.find(t => t.id === _draft.categoryId);
  const subOpts = (selTop ? (selTop.children || []) : []).map(c =>
    `<option value="${htmlEscape(c.id)}" ${c.id === _draft.subcategoryId ? 'selected' : ''}>${htmlEscape(c.name)}</option>`).join('');

  return `
    <div style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:12px 14px;">
      <strong>Identität</strong>
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px 12px; margin-top:8px;">
        <label class="med-row"><span class="med-label">Name *</span>
          <input type="text" value="${htmlEscape(_draft.name)}" oninput="cfgSet('name', this.value)"></label>
        <label class="med-row"><span class="med-label">Preis (€)</span>
          <input type="number" step="0.10" value="${_draft.price}" oninput="cfgSet('price', this.value)"></label>
        <label class="med-row"><span class="med-label">Rubrik *</span>
          <select onchange="cfgSetCategory(this.value)"><option value="">— wählen —</option>${topOpts}</select></label>
        <label class="med-row"><span class="med-label">Unter-Rubrik</span>
          <select onchange="cfgSet('subcategoryId', this.value)" ${selTop ? '' : 'disabled'}>
            <option value="">— keine —</option>${subOpts}</select></label>
        <label class="med-row"><span class="med-label">Typ</span>
          <select onchange="cfgSet('itemType', this.value)">
            ${ITEM_TYPES.map(t => `<option value="${t.v}" ${_draft.itemType === t.v ? 'selected' : ''}>${t.label}</option>`).join('')}</select></label>
        <label class="med-row"><span class="med-label">Einheit</span>
          <input type="text" value="${htmlEscape(_draft.unitLabel)}" placeholder="z.B. 0,4l / 150g" oninput="cfgSet('unitLabel', this.value)"></label>
      </div>
      <label class="med-row" style="margin-top:6px;"><span class="med-label">Beschreibung</span>
        <textarea rows="2" oninput="cfgSet('description', this.value)">${htmlEscape(_draft.description)}</textarea></label>
      <div style="margin-top:8px; display:flex; gap:14px; align-items:center; flex-wrap:wrap;">
        <label class="checkbox"><input type="checkbox" ${_draft.available ? 'checked' : ''} onchange="cfgSet('available', this.checked)"> aktiv</label>
        <span class="hint" style="font-size:.8rem;">Diät-Tags (Karte, LMIV — nur bestätigen, was zutrifft):</span>
      </div>
      <div class="med-tag-list" style="margin-top:4px;">
        ${DISH_DIET_TAGS.map(t => `
          <label class="med-tag-chip ${_draft.dietaryTags.includes(t) ? 'active' : ''}">
            <input type="checkbox" ${_draft.dietaryTags.includes(t) ? 'checked' : ''} onchange="cfgToggleTag('${t}', this.checked)"> ${t}
          </label>`).join('')}
      </div>
    </div>`;
}

function catalog() {
  const c = window.App && window.App.nutritionCache && window.App.nutritionCache.getCatalog && window.App.nutritionCache.getCatalog();
  return c || { ingredientsById: {}, recipesByItem: {} };
}

// S156c — Default-Option-Dropdown (Bun/Patty) mit Constraint-Filter. Bei aktivem
// Diät-Ziel nur konforme Optionen anbieten; eine gewählte, aber verletzende Option
// bleibt (non-destruktiv) + wird rot markiert.
function renderDefaultSelect(g, label, selectedName, handler) {
  const cat = catalog();
  const allowed = filterOptionsByTargets(g.options, cat.recipesByItem, cat.ingredientsById, _targets);
  const allowedIds = new Set(allowed.map(o => o.id));
  const filterOn = constraintFilterActive(_targets);
  const list = g.options.filter(o => !filterOn || allowedIds.has(o.id) || o.name === selectedName);
  const selOpt = g.options.find(o => o.name === selectedName);
  const violates = !!(filterOn && selOpt && !allowedIds.has(selOpt.id));
  return `
    <span class="hint" style="font-size:.8rem;">${label}:</span>
    <select onchange="${handler}('${htmlEscape(g.group)}', this.value)" ${violates ? 'style="border-color:var(--danger,#b94a48);" title="Verstößt gegen das aktive Ziel"' : ''}>
      <option value="">— erstes —</option>
      ${list.map(o => `<option value="${htmlEscape(o.name)}" ${selectedName === o.name ? 'selected' : ''}>${htmlEscape(o.name)}</option>`).join('')}
    </select>
    ${violates ? '<span class="hint" style="color:var(--danger,#b94a48); font-size:.72rem;">⚠︎ Verstoß</span>' : ''}`;
}

/** S193b — aufgelöste Regeln eines Satzes AM GERICHT für die Inline-Controls:
 *  aus _draft.meta.optionSets, sonst Rollen-Default (= heutiges Verhalten). */
function _rulesForGroupInDraft(group, role) {
  const e = optionSetsFromMeta(_draft && _draft.meta).find(s => s && s.group === group);
  if (e) return { required: !!e.required, multi: !!e.multi, min: e.min, max: e.max };
  return { ...defaultRulesForRole(role), };
}

/** S193-Fix2 — Default-Item eines Satzes AM GERICHT (aus optionSets.defaultItem). */
function _defaultItemFor(group) {
  const e = optionSetsFromMeta(_draft && _draft.meta).find(s => s && s.group === group);
  return (e && e.defaultItem) || '';
}

/** S193b — Inline-Regel-Controls je aktiviertem Optionssatz (dish-driven).
 *  Pflicht/Mehrfach als Toggles (wie der Default), min/max kompakt. Schreiben
 *  meta.optionSets am GERICHT (nicht an der Gruppe). */
function renderGroupRulesHtml(g) {
  const r = _rulesForGroupInDraft(g.group, g.role);
  const gk = htmlEscape(g.group);
  return `
    <span class="cfg-rule-group" style="display:inline-flex; gap:10px; align-items:center; flex-wrap:wrap;">
      <label class="checkbox" style="font-size:.82rem;" title="Muss der Gast wählen?">
        <input type="checkbox" ${r.required ? 'checked' : ''} onchange="cfgSetRule('${gk}','required', this.checked)"> Pflicht
      </label>
      <label class="checkbox" style="font-size:.82rem;" title="Mehrere gleichzeitig wählbar?">
        <input type="checkbox" ${r.multi ? 'checked' : ''} onchange="cfgSetRule('${gk}','multi', this.checked)"> Mehrfach
      </label>
      <span class="hint" style="font-size:.8rem;">min
        <input type="number" min="0" step="1" style="width:52px;" value="${r.min != null ? r.min : ''}" oninput="cfgSetRule('${gk}','min', this.value)"></span>
      <span class="hint" style="font-size:.8rem;">max
        <input type="number" min="0" step="1" style="width:52px;" value="${r.max != null ? r.max : ''}" oninput="cfgSetRule('${gk}','max', this.value)"></span>
    </span>`;
}

function renderGroupsHtml() {
  if (!_groups.length) return '';
  const ROLE_LABEL = { bun: 'Brötchen', patty: 'Patty', extras: 'Extras', dressing: 'Dressing', option: 'Option' };
  return `
    <div style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:12px 14px;">
      <strong>Options-Gruppen & Regeln</strong>
      <p class="hint" style="margin:4px 0 8px;">Welche modularen Gruppen hat das Gericht? Pflicht/Mehrfach/min/max entscheidet das GERICHT je Satz (schreibt meta.optionSets).</p>
      <div style="display:flex; flex-direction:column; gap:6px;">
        ${_groups.map(g => {
          const sel = _selection.find(s => s.group === g.group) || { on: false };
          return `
            <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap;">
              <label class="checkbox" style="min-width:200px;">
                <input type="checkbox" ${sel.on ? 'checked' : ''} onchange="cfgToggleGroup('${htmlEscape(g.group)}', this.checked)">
                <strong>${htmlEscape(g.label)}</strong> <span class="hint" style="font-size:.76rem;">(${ROLE_LABEL[g.role] || g.role})</span>
              </label>
              ${g.role === 'bun' && sel.on ? renderDefaultSelect(g, 'Default-Brötchen', sel.defaultBun, 'cfgSetDefaultBun') : ''}
              ${g.role === 'patty' && sel.on ? renderDefaultSelect(g, 'Default-Patty', sel.defaultPatty, 'cfgSetDefaultPatty') : ''}
              ${g.role !== 'bun' && g.role !== 'patty' && sel.on ? renderDefaultSelect(g, 'Default', _defaultItemFor(g.group), 'cfgSetDefaultItem') : ''}
              ${sel.on ? renderGroupRulesHtml(g) : ''}
            </div>`;
        }).join('')}
      </div>
    </div>`;
}

// S158 — Food-Cost-Zeile (Wareneinsatz · FC% · Marge · Ampel) aus dem Live-Cost.
function renderFoodCostHtml(cost) {
  if (!cost || !cost.ingredientCount) {
    return `<span class="hint" style="font-size:.8rem;">Food-Cost:</span> <strong>—</strong> <span class="hint" style="font-size:.76rem;">(Zutaten + Einkaufspreise pflegen)</span>`;
  }
  const price = Number(_draft.price);
  const target = targetPctFor(currentMeta(), globalFoodCostTarget());
  const ev = costEvaluation(cost.goodsCost, price, target);
  // S158-Hotfix: bei unvollständigen Einkaufsdaten grau (Teilwert), nicht grün.
  const band = ampelFor({ pct: ev.pct, missingData: cost.missingData, targetPct: target });
  const dot = `<span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:${AMPEL_COLOR[band]}; vertical-align:middle;" title="${AMPEL_LABEL[band]} (Ziel ≤ ${target} %)"></span>`;
  return `
    <span class="hint" style="font-size:.8rem;">Wareneinsatz</span> <strong>${formatEuro(ev.goodsCost)}</strong>
    <span class="hint" style="font-size:.8rem;"> · Food-Cost</span> <strong>${formatPct(ev.pct)}</strong> ${dot}
    <span class="hint" style="font-size:.8rem;"> · Marge</span> <strong>${formatEuro(ev.margin)}</strong>
    ${cost.missingData ? '<span style="color:var(--danger,#b94a48); font-size:.78rem;"> · ⚠️ Einkaufsdaten unvollständig</span>' : ''}`;
}

function renderLiveHtml(n, cost) {
  const constraintChips = evalConstraints(_targets, { dietaryTags: (n && n.dietaryTags) || [], energyKcal: (n && n.energyKcal) || 0 });
  const targetChip = (key, label) => `
    <label class="med-tag-chip ${_targets[key] ? 'active' : ''}" style="cursor:pointer;">
      <input type="checkbox" ${_targets[key] ? 'checked' : ''} onchange="cfgToggleTarget('${key}', this.checked)"> ${label}
    </label>`;
  return `
    <div style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:12px 14px; background:var(--bg-soft,#f6f6f6);">
      <strong>Live-Werte (Default-Komposition)</strong>
      <div style="margin-top:8px;">${n ? renderNutritionBoxHtml(n) : '<span class="hint">Zutaten zuordnen für Live-Nährwerte…</span>'}</div>
      <div style="margin-top:10px;">${renderFoodCostHtml(cost)}</div>
      <div style="margin-top:12px; border-top:1px dashed var(--border,#ddd); padding-top:10px;">
        <div class="hint" style="font-size:.8rem; margin-bottom:4px;">Ziel-Check (assistiv, blockiert nicht):</div>
        <div class="med-tag-list">
          ${targetChip('vegan', 'Vegan')} ${targetChip('vegetarian', 'Vegetarisch')}
          ${targetChip('gluten_free', 'Glutenfrei')} ${targetChip('lactose_free', 'Laktosefrei')}
          <label class="med-tag-chip" style="display:inline-flex; align-items:center; gap:4px;">≤
            <input type="number" min="0" step="50" value="${_targets.maxKcal}" style="width:64px;" oninput="cfgSetMaxKcal(this.value)"> kcal</label>
        </div>
        ${constraintChips.length ? `<div style="margin-top:8px; display:flex; gap:6px; flex-wrap:wrap;">
          ${constraintChips.map(c => `<span style="font-size:.8rem; padding:2px 8px; border-radius:10px; background:${c.ok ? 'var(--green-soft)' : 'var(--danger-soft)'}; color:${c.ok ? 'var(--green)' : 'var(--danger)'};">${c.ok ? '✓' : '✗'} ${htmlEscape(c.label)}</span>`).join('')}
        </div>` : ''}
        <div class="hint" style="font-size:.72rem; margin-top:6px;">LMIV-Hinweis: Diätetische Auszeichnung erscheint nur auf der Karte, wenn du sie oben in den Diät-Tags bestätigst.</div>
      </div>
    </div>`;
}

function renderAdvancedHtml() {
  return `
    <details style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:8px 14px;">
      <summary style="cursor:pointer; font-weight:600;">Erweitert (Power-User)</summary>
      <div style="margin-top:8px;">
        <label class="med-row"><span class="med-label">Zutaten-Freitext <small>(Gast-Streichliste im Picker, Komma-getrennt)</small></span>
          <textarea rows="2" oninput="cfgSet('ingredientsText', this.value)">${htmlEscape(_draft.ingredientsText)}</textarea></label>
        <label class="med-row"><span class="med-label">Meta (JSON) <small>(wird beim Speichern aus den Options-Gruppen überschrieben; nur für Sonderfälle)</small></span>
          <textarea rows="3" class="me-meta" oninput="cfgSetMetaJson(this.value)">${htmlEscape(JSON.stringify(currentMeta(), null, 2))}</textarea></label>
        <label class="med-row"><span class="med-label">Uber-Eats-Item-ID</span>
          <input type="text" value="${htmlEscape(_draft.uberItemId)}" oninput="cfgSet('uberItemId', this.value)"></label>
      </div>
    </details>`;
}

// -----------------------------------------------------------------------------
// Teil-Refresh
// -----------------------------------------------------------------------------
function refreshLive() {
  const host = document.getElementById('cfg-live');
  if (!host) return;
  let n = null, cost = null;
  try {
    const cache = window.App && window.App.nutritionCache;
    const rows = getRecipeCleanRows();
    const draft = { meta: currentMeta() };
    if (cache && cache.composeDraftSync) n = cache.composeDraftSync(rows, draft);
    if (cache && cache.composeCostDraftSync) cost = cache.composeCostDraftSync(rows, draft); // S158
  } catch (_) {}
  host.innerHTML = renderLiveHtml(n, cost);
}

// -----------------------------------------------------------------------------
// Handler
// -----------------------------------------------------------------------------
export function cfgSet(field, value) {
  if (!_draft) return;
  _dirty = true;
  if (field === 'available') _draft.available = !!value;
  else _draft[field] = value;
  if (field === 'name') _touched.name = true;          // S156b/B — manuell gesetzt
  if (field === 'subcategoryId') _touched.category = true;
  if (field === 'price') refreshLive();                // S158 — Preisänderung → Ampel live (nur #cfg-live, Fokus bleibt)
}
export function cfgSetCategory(catId) {
  if (!_draft) return;
  _dirty = true;
  _draft.categoryId = catId || '';
  _draft.subcategoryId = '';
  const top = buildCategoryTree(_categories).find(t => t.id === catId);
  _draft.category = top ? top.name : '';
  _touched.category = true; // S156b/B
  rerenderShell(); // Unter-Rubrik-Dropdown aktualisieren
}
export function cfgToggleTag(tag, on) {
  if (!_draft) return;
  _dirty = true;
  const i = _draft.dietaryTags.indexOf(tag);
  if (on && i < 0) _draft.dietaryTags.push(tag);
  if (!on && i >= 0) _draft.dietaryTags.splice(i, 1);
}
export function cfgToggleGroup(group, on) {
  _dirty = true;
  let sel = _selection.find(s => s.group === group);
  if (!sel) { const g = _groups.find(x => x.group === group); sel = { group, role: g ? g.role : 'option', on: false, defaultPatty: null, defaultBun: null }; _selection.push(sel); }
  sel.on = !!on;
  // S193b — optionSets am Gericht mitführen: aktivierter Satz bekommt einen
  // Regel-Eintrag (Rollen-Default = heutiges Verhalten), deaktivierter fliegt raus.
  const arr = _optionSetsArr();
  const idx = arr.findIndex(s => s && s.group === group);
  if (on && idx < 0) {
    const role = sel.role || 'option';
    const def = defaultRulesForRole(role);
    arr.push({ group, role, required: def.required, multi: def.multi, min: def.min, max: def.max, defaultItem: null, sort: arr.length });
  } else if (!on && idx >= 0) {
    arr.splice(idx, 1);
  }
  _touched.options = true; // S156b/B
  rerenderShell();
}

/** S193b — _draft.meta.optionSets robust als Array beschaffen. */
function _optionSetsArr() {
  if (!_draft.meta || typeof _draft.meta !== 'object') _draft.meta = {};
  if (!Array.isArray(_draft.meta.optionSets)) _draft.meta.optionSets = [];
  return _draft.meta.optionSets;
}

/** S193b — Regel eines Optionssatzes AM GERICHT setzen (Pflicht/Mehrfach/min/max).
 *  Schreibt meta.optionSets; refreshLive (kein Picker-Re-Render → Fokus bleibt). */
export function cfgSetRule(group, field, value) {
  if (!_draft) return;
  _dirty = true; _touched.options = true;
  const arr = _optionSetsArr();
  let e = arr.find(s => s && s.group === group);
  if (!e) {
    const g = _groups.find(x => x.group === group);
    const role = g ? g.role : 'option';
    const def = defaultRulesForRole(role);
    e = { group, role, required: def.required, multi: def.multi, min: def.min, max: def.max, defaultItem: null, sort: arr.length };
    arr.push(e);
  }
  if (field === 'required' || field === 'multi') {
    e[field] = !!value;
  } else if (field === 'min' || field === 'max') {
    e[field] = (value === '' || value == null || !isFinite(Number(value))) ? null : Math.max(0, Math.floor(Number(value)));
  }
  refreshLive();
}

/** S193-Fix2 — Default-Item eines Satzes AM GERICHT setzen (optionSets.defaultItem),
 *  konsistent mit Default-Brötchen/-Patty; bei optional/Mehrfach leer erlaubt. */
export function cfgSetDefaultItem(group, name) {
  if (!_draft) return;
  _dirty = true; _touched.options = true;
  const arr = _optionSetsArr();
  let e = arr.find(s => s && s.group === group);
  if (!e) {
    const g = _groups.find(x => x.group === group);
    const role = g ? g.role : 'option';
    const def = defaultRulesForRole(role);
    e = { group, role, required: def.required, multi: def.multi, min: def.min, max: def.max, defaultItem: null, sort: arr.length };
    arr.push(e);
  }
  e.defaultItem = name || null;
  refreshLive();
}
export function cfgSetDefaultPatty(group, name) {
  const sel = _selection.find(s => s.group === group);
  if (sel) sel.defaultPatty = name || null;
  _touched.options = true; // S156b/B
  _dirty = true;
  refreshLive();
}
export function cfgSetDefaultBun(group, name) { // S156c
  const sel = _selection.find(s => s.group === group);
  if (sel) sel.defaultBun = name || null;
  _touched.options = true;
  _dirty = true;
  refreshLive();
}
export function cfgToggleTarget(key, on) {
  _targets[key] = !!on;
  applyIngredientFilter(); // S156c — Zutaten-Dropdowns auf konforme filtern
  rerenderShell();         // Dropdowns + rote Markierungen neu, Live folgt
}

// S156c — Constraint-Filter an den eingebetteten Rezept-Editor reichen. Bei aktivem
// Diät-Ziel nur konforme Zutaten anbieten; sonst Filter aus (Auswahl wieder vollständig).
function applyIngredientFilter() {
  if (constraintFilterActive(_targets)) {
    const t = { ..._targets };
    setIngredientFilter(ing => ingredientMeetsTargets(ing, t));
  } else {
    setIngredientFilter(null);
  }
}
export function cfgSetMaxKcal(val) { _targets.maxKcal = val; refreshLive(); }
export function cfgSetMetaJson(json) {
  _dirty = true;
  try { _draft.meta = json && json.trim() ? JSON.parse(json) : {}; }
  catch (_) { /* ungültig → ignorieren bis valide */ }
}

// Re-Render des ganzen Modalinhalts (für strukturelle Änderungen: Rubrik/Gruppen).
// Rezept-State bleibt erhalten (menu-recipe-editor-Modul hält ihn); Live danach neu.
function rerenderShell() {
  const content = _cfgContentEl();
  if (!content) return;
  content.innerHTML = renderConfiguratorHtml();
  refreshLive();
}

export async function cfgSave() {
  if (!_draft) return;
  const meta = currentMeta();
  const { valid, errors, payload } = buildMenuItemPayload({ ..._draft, meta });
  if (!valid) { showToast('Bitte ausfüllen: ' + errors.join(', '), 'error'); return; }
  // Legacy-Felder + Freitext-Zutaten erhalten.
  payload.ingredients = (_draft.ingredientsText || '').split(',').map(s => s.trim()).filter(Boolean);
  payload.uberItemId = (_draft.uberItemId || '').trim() || null; // S193-Fix2 — leer = NULL (UNIQUE-Index)

  try {
    let id = _draft.id;
    if (id) {
      await storage().menu.update(id, payload);
    } else {
      const sameRubrik = menuState().filter(m => m.categoryId === _draft.categoryId || m.category === _draft.category);
      payload.displayOrder = (sameRubrik.reduce((mx, m) => Math.max(mx, m.displayOrder || 0), 0) || 0) + 10;
      const created = await storage().menu.create(payload);
      id = created.id;
    }
    await storage().recipes.setItems(id, getRecipeCleanRows());
    if (window.App && window.App.nutritionCache) window.App.nutritionCache.invalidate();
    showToast('Gericht gespeichert', 'success');
    setRecipeChangeHook(null);
    setIngredientFilter(null); // S156c — Filter nicht in den Standalone-Rezept-Editor leaken
    _dirty = false;
    if (_present === 'slideover') {
      // S187 Cut B — Slide-over schließt bei Erfolg; Studio-Liste + Vorschau ziehen
      // non-destruktiv nach (Item-Cache ist durch menu.update/create bereits frisch).
      _closeSlideover();
      if (typeof window.studioRefreshAfterItemSave === 'function') window.studioRefreshAfterItemSave();
    } else {
      if (typeof window.closeModal === 'function') window.closeModal();
      if (typeof window.renderMenuList === 'function') window.renderMenuList();
    }
  } catch (e) {
    console.error('[configurator] save:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// S156 — Generativ („🎲 Zufällig konfigurieren")
// -----------------------------------------------------------------------------
function inferKind() {
  const cat = (_draft.category || '').toLowerCase();
  if (cat.includes('burger')) return 'burger';
  if (cat.includes('bowl')) return 'bowl';
  if (cat.includes('salat')) return 'salad';
  if (cat.includes('getränk') || cat.includes('getrank')) return 'drink';
  if (cat.includes('beilage')) return 'side';
  if (cat.includes('soße') || cat.includes('sosse')) return 'sauce';
  if (_draft.itemType === 'drink') return 'drink';
  if (_draft.itemType === 'side') return 'side';
  if (_draft.itemType === 'sauce') return 'sauce';
  if (_groups.some(g => g.role === 'bun')) return 'burger';
  return 'salad';
}

// Default-Option-IDs (Bun/Patty) der aktuellen Gruppen-Auswahl — für keep beim
// Lücken-Füllen (Generator soll die getouchten Optionen nicht neu würfeln).
function currentOptionDefaults() {
  const out = { bun: null, patty: null };
  const bunSel = _selection.find(s => s.on && s.role === 'bun');
  if (bunSel) {
    const g = _groups.find(x => x.group === bunSel.group);
    const o = g && ((bunSel.defaultBun && g.options.find(x => x.name === bunSel.defaultBun)) || g.options.find(x => x.isDefault) || g.options[0]);
    if (o) out.bun = o.id;
  }
  const pattySel = _selection.find(s => s.on && s.role === 'patty');
  if (pattySel) {
    const g = _groups.find(x => x.group === pattySel.group);
    const o = g && ((pattySel.defaultPatty && g.options.find(x => x.name === pattySel.defaultPatty)) || g.options[0]);
    if (o) out.patty = o.id;
  }
  return out;
}

// S156b/B — gemeinsamer Generierungs-Pfad. fillGaps=true → getouchte Felder bleiben
// erhalten (nur offene Slots würfeln); fillGaps=false → kompletter Re-Roll.
function doGenerate(fillGaps) {
  if (!_draft) return;
  const cache = window.App && window.App.nutritionCache;
  if (!cache || !cache.getCatalog) { showToast('Katalog nicht geladen.', 'error'); return; }
  const { ingredientsById, recipesByItem } = cache.getCatalog();
  if (!ingredientsById || !Object.keys(ingredientsById).length) { showToast('Noch keine Zutaten im Katalog.', 'error'); return; }

  const constraints = {
    vegan: _targets.vegan, vegetarian: _targets.vegetarian,
    gluten_free: _targets.gluten_free, lactose_free: _targets.lactose_free, maxKcal: _targets.maxKcal
  };
  const keep = {};
  if (fillGaps) {
    if (_touched.name && _draft.name) keep.name = _draft.name;
    if (_touched.baseRows) keep.baseRows = getRecipeCleanRows();
    if (_touched.options) {
      const opt = currentOptionDefaults();
      if (opt.bun) keep.bun = opt.bun;
      if (opt.patty) keep.patty = opt.patty;
    }
  }

  let res;
  try {
    res = generateDish({ kind: inferKind(), optionGroups: _groups, constraints, ingredientsById, recipesByItem, keep });
  } catch (e) {
    console.error('[configurator] generate:', e);
    showToast('Generieren fehlgeschlagen: ' + (e.message || e), 'error');
    return;
  }

  // Entwurf in die Maske übernehmen (alles editierbar, nichts gespeichert). Getouchte
  // Felder bleiben beim Lücken-Füllen unverändert; „Alles neu" überschreibt alles.
  if (!(fillGaps && _touched.baseRows)) setRecipeRows(res.baseRows);
  if (!(fillGaps && _touched.options)) {
    _draft.meta = res.meta || {};
    _selection = groupSelectionFromMeta(_draft.meta, _groups);
  }
  if (!(fillGaps && _touched.name) && res.suggestedName) _draft.name = res.suggestedName;
  // Diät-Tags = Vorschlag (LMIV: erst durch Speichern bestätigt).
  _draft.dietaryTags = Array.isArray(res.dietaryTags) ? res.dietaryTags.slice() : [];

  if (!fillGaps) _touched = { name: false, category: _touched.category, options: false, baseRows: false };
  _dirty = true;   // Generator-Entwurf = ungespeicherte Änderung
  rerenderShell();
  showToast(fillGaps ? 'Offene Felder gewürfelt — frei anpassbar' : 'Entwurf neu gewürfelt — frei anpassbar', 'success');
}

export function cfgGenerate() { doGenerate(true); }       // Lücken füllen (respektiert touched)
export function cfgGenerateAll() { doGenerate(false); }    // voller Re-Roll

export function cfgCancel() {
  if (_present === 'slideover') { _requestCloseSlideover(); return; }  // Draft-Schutz
  setRecipeChangeHook(null);
  setIngredientFilter(null); // S156c
  if (typeof window.closeModal === 'function') window.closeModal();
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openConfigurator, cfgSet, cfgSetCategory, cfgToggleTag, cfgToggleGroup, cfgSetRule, cfgSetDefaultItem,
    cfgSetDefaultPatty, cfgSetDefaultBun, cfgToggleTarget, cfgSetMaxKcal, cfgSetMetaJson, cfgSave, cfgCancel,
    cfgGenerate, cfgGenerateAll
  });
}
